import React from "react";
import { InputAdornment } from "@mui/material";
import { MuiDecimalField } from "../../../../../components/formsComponents/MuiDecimalField";
import {
  DiscountModeSelector,
  DiscountMode,
} from "./DiscountModeSelector";

interface IDiscountValueFieldProps {
  mode: DiscountMode;
  value: number | string | null | undefined;
  onModeChange: (next: DiscountMode) => void;
  onChange: (value: string) => void;
  name?: string;
  disabled?: boolean;
}

/**
 * Unified discount field that merges the discount *mode* (percentage / amount)
 * and the discount *value* into a single control — the discount counterpart of
 * TaxPercentageField. Shares the same MuiDecimalField base so it renders at the
 * identical height/padding as the Tax field.
 *
 * The mode selector ("% ▾" / "₹ ▾") lives in the RIGHT (end) adornment; the
 * value is always editable. Percentage mode caps the value at 100.
 */
export const DiscountValueField: React.FC<IDiscountValueFieldProps> = ({
  mode,
  value,
  onModeChange,
  onChange,
  name = "discount",
  disabled = false,
}) => {
  const isPercentage = mode === "percentage";

  return (
    <MuiDecimalField
      name={name}
      value={value}
      max={isPercentage ? 100 : undefined}
      placeholder="0"
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        minWidth: 90,
        maxWidth: 140,
        // No left padding — there is no start adornment, so the value hugs the
        // left border. Only a little right padding before the mode selector.
        // Fixed 36px field height to match the Tax field.
        "& .MuiOutlinedInput-root": {
          pl: 0,
          pr: 0.5,
          height: 36,
        },
        // Light-bordered box around the editable value (matches Tax field).
        "& .MuiInputBase-input": {
          flex: 1,
          minWidth: 0,
          width: "auto",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          textAlign: "right",
          ml: 0,
          mr: 0.5,
          px: 0.5,
          py: 0,
          height: 36,
          boxSizing: "border-box",
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ ml: 0 }}>
            <DiscountModeSelector
              value={mode}
              onChange={onModeChange}
              disabled={disabled}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};
